import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, RotateCcw, Eye, Download, Truck, Pencil } from "lucide-react";
import { toast, Toaster } from "sonner";
import HandoverModal from "@/components/HandoverModal";
import ContractEditModal from "@/components/ContractEditModal";

const STATUS_FLOW = [
  { id: "pending", labelKey: "status_pending" },
  { id: "ready", labelKey: "status_ready" },
  { id: "handed_over", labelKey: "status_handed_over" },
  { id: "returned", labelKey: "status_returned" },
  { id: "completed", labelKey: "status_completed" },
];

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-[var(--rs-surface-2)] text-[var(--rs-text)]",
    ready: "bg-[var(--rs-accent)]/20 text-[var(--rs-accent)]",
    handed_over: "bg-[var(--rs-primary)] text-white",
    returned: "bg-black text-white",
    completed: "bg-[var(--rs-primary)]/15 text-[var(--rs-primary)]",
    cancelled: "bg-[var(--rs-error)]/15 text-[var(--rs-error)]",
  };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 ${map[status] || map.pending}`}>
      {status?.replace("_", " ") || "—"}
    </span>
  );
};

const StationDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stations, setStations] = useState([]);
  const [filter, setFilter] = useState("active"); // active = pending+ready+handed_over
  const [modal, setModal] = useState({ open: false, mode: null, contract: null });
  const [editModal, setEditModal] = useState({ open: false, contract: null });

  const load = async () => {
    try {
      const { data } = await api.get("/contracts");
      setContracts(data);
    } catch (e) {
      toast.error("Fehler beim Laden");
    }
  };

  useEffect(() => {
    load();
    api.get("/vehicles").then(({ data }) => {
      setVehicles(data.vehicles || []);
      setStations(data.stations || []);
    }).catch(() => {});
  }, []);

  const visible = contracts.filter((c) => {
    if (filter === "active") return ["pending", "ready", "handed_over"].includes(c.status);
    if (filter === "all") return true;
    return c.status === filter;
  });

  const setStatus = async (c, newStatus, extra = {}) => {
    try {
      await api.patch(`/contracts/${c.id}/status`, { status: newStatus, ...extra });
      toast.success(`Status: ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const promptOdo = (c, target) => {
    if (target === "handed_over") {
      setModal({ open: true, mode: "handover", contract: c });
      return;
    }
    if (target === "returned") {
      setModal({ open: true, mode: "return", contract: c });
      return;
    }
    setStatus(c, target);
  };

  const onModalConfirm = ({ odometer, photoBase64, additionalPhotos, actualTime, overtimeHours }) => {
    const c = modal.contract;
    const target = modal.mode === "handover" ? "handed_over" : "returned";
    const extra = {};
    if (modal.mode === "handover") {
      extra.odometer_start = odometer;
      if (photoBase64) extra.handover_photo_base64 = photoBase64;
    } else {
      extra.odometer_end = odometer;
      if (photoBase64) extra.return_photo_base64 = photoBase64;
      if (overtimeHours && overtimeHours > 0) extra.overtime_hours = overtimeHours;
    }
    if (additionalPhotos && additionalPhotos.length > 0) {
      extra.additional_photos = additionalPhotos;
    }

    const persistAndStatus = async () => {
      try {
        if (actualTime) {
          const patchBody = modal.mode === "handover"
            ? { rental_start: actualTime }
            : { rental_end: actualTime };
          await api.patch(`/contracts/${c.id}`, patchBody);
        }
      } catch (e) {
        // non-fatal
      }
      setStatus(c, target, extra);
      setModal({ open: false, mode: null, contract: null });
    };
    persistAndStatus();
  };

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" });
      // Defensive: if server returned JSON error wrapped as blob (HTTP 200 but JSON body), surface it
      if (res.data && res.data.type && res.data.type.includes("json")) {
        const text = await res.data.text();
        throw new Error(text || "Server-Fehler");
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.contract_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "PDF-Download fehlgeschlagen";
      // Try to read error message from blob if it was a JSON error
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          if (json?.detail) msg = String(json.detail);
        } else if (e?.response?.data?.detail) {
          msg = String(e.response.data.detail);
        }
      } catch {}
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("PDF error:", e);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="rs-overline">NIVARA · STATION</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-2">
          <div>
            <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight leading-[1]">
              {t("station_title")}
            </h1>
            <p className="text-[var(--rs-text-muted)] mt-2">{t("station_sub")}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { id: "active", label: "Aktive" },
              { id: "all", label: "Alle" },
              { id: "completed", label: "Abgeschlossen" },
            ].map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setFilter(b.id)}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.15em] border ${
                  filter === b.id
                    ? "bg-[var(--rs-primary)] text-white border-[var(--rs-primary)]"
                    : "border-[var(--rs-border)] text-[var(--rs-text-muted)] hover:border-[var(--rs-primary)]"
                }`}
                data-testid={`station-filter-${b.id}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="mt-12 text-center py-20 border-2 border-dashed border-[var(--rs-border)]">
            <Truck size={32} className="mx-auto text-[var(--rs-text-muted)]" />
            <div className="rs-overline mt-3">Keine Verträge in dieser Ansicht</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
            {visible.map((c) => (
              <article key={c.id} className="bg-white border-2 border-[var(--rs-text)]" data-testid={`station-card-${c.id}`}>
                <header className="flex items-start justify-between gap-4 p-5 border-b border-[var(--rs-border)] bg-[var(--rs-surface)]">
                  <div>
                    <div className="text-xs font-bold tracking-tight">{c.contract_number}</div>
                    <h3 className="font-[Chivo] text-lg font-black mt-1">{c.customer_name}</h3>
                  </div>
                  <StatusBadge status={c.status} />
                </header>

                <div className="p-5 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="rs-overline">{t("th_vehicle")}</div>
                    <div className="font-bold mt-1">{c.vehicle_name}</div>
                    <div className="text-xs text-[var(--rs-text-muted)]">{c.vehicle_plate}</div>
                  </div>
                  <div>
                    <div className="rs-overline">Tarif</div>
                    <div className="font-bold mt-1">
                      {c.rental_type === "master_package"
                        ? `Master ${c.package_hours || ""}h`
                        : c.rental_type === "vito_daily"
                        ? `Vito ${(c.destination || "ch").toUpperCase()} · ${c.days || c.duration_days || 1}T`
                        : c.rental_type}
                    </div>
                    {c.duration_hours > 0 && (
                      <div className="text-xs text-[var(--rs-text-muted)]">{Number(c.duration_hours).toFixed(1)} h</div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <div className="rs-overline">{t("th_period")}</div>
                    <div className="text-xs mt-1">
                      {(c.rental_start || "").replace("T", " ")} → {(c.rental_end || "").replace("T", " ")}
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center justify-between border-t border-[var(--rs-border)] pt-3">
                    <span className="rs-overline">{t("total_price")}</span>
                    <span className="font-[Chivo] text-xl font-black text-[var(--rs-primary)]">CHF {c.price_chf}.–</span>
                  </div>
                </div>

                <div className="px-5 pb-5 flex flex-wrap gap-2">
                  {c.status === "pending" && (
                    <button type="button" className="rs-btn-secondary !py-3 !px-4 !text-xs" onClick={() => setStatus(c, "ready")} data-testid={`mark-ready-${c.id}`}>
                      <CheckCircle2 size={14} className="mr-2" /> {t("mark_ready")}
                    </button>
                  )}
                  {c.status === "ready" && (
                    <button type="button" className="rs-btn-primary !py-3 !px-4 !text-xs" onClick={() => promptOdo(c, "handed_over")} data-testid={`mark-handed-${c.id}`}>
                      <ArrowRight size={14} className="mr-2" /> {t("mark_handed_over")}
                    </button>
                  )}
                  {c.status === "handed_over" && (
                    <button type="button" className="rs-btn-primary !py-3 !px-4 !text-xs" onClick={() => promptOdo(c, "returned")} data-testid={`mark-returned-${c.id}`}>
                      <RotateCcw size={14} className="mr-2" /> {t("mark_returned")}
                    </button>
                  )}
                  {c.status === "returned" && (
                    <button type="button" className="rs-btn-primary !py-3 !px-4 !text-xs" onClick={() => setStatus(c, "completed")} data-testid={`mark-completed-${c.id}`}>
                      <CheckCircle2 size={14} className="mr-2" /> {t("mark_completed")}
                    </button>
                  )}
                  <button type="button" className="rs-btn-ghost !text-xs" onClick={() => navigate(`/station/contracts/${c.id}`)} data-testid={`station-view-${c.id}`}>
                    <Eye size={14} className="mr-1" /> {t("view")}
                  </button>
                  <button type="button" className="rs-btn-ghost !text-xs" onClick={() => downloadPdf(c)} data-testid={`station-pdf-${c.id}`}>
                    <Download size={14} className="mr-1" /> PDF
                  </button>
                  <button type="button" className="rs-btn-ghost !text-xs" onClick={() => setEditModal({ open: true, contract: c })} data-testid={`station-edit-${c.id}`}>
                    <Pencil size={14} className="mr-1" /> Bearbeiten
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <HandoverModal
        open={modal.open}
        mode={modal.mode}
        vehicleName={modal.contract?.vehicle_name || ""}
        defaultTime={(modal.mode === "handover" ? modal.contract?.rental_start : modal.contract?.rental_end) || ""}
        isMaster={modal.contract?.rental_type === "master_package"}
        onClose={() => setModal({ open: false, mode: null, contract: null })}
        onConfirm={onModalConfirm}
      />
      <ContractEditModal
        open={editModal.open}
        contract={editModal.contract}
        vehicles={vehicles}
        stations={stations}
        onClose={() => setEditModal({ open: false, contract: null })}
        onSaved={load}
      />
    </div>
  );
};

export default StationDashboard;
